import React, { useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import '../booking/ServicePage.css';

const FLEET_SIZES = [
  { id: '1-3', label: '1–3 cars', sub: 'Small baraat' },
  { id: '4-8', label: '4–8 cars', sub: 'Medium baraat' },
  { id: '9-15', label: '9–15 vehicles', sub: 'Large baraat' },
  { id: 'custom', label: 'Custom fleet', sub: 'Specify below' },
];

const VEHICLE_OPTIONS = [
  { id: 'sedan', name: 'Decorated Sedans', seats: 4, decorated: true, price: 1800, per: '/ vehicle / day' },
  { id: 'suv', name: 'SUV (Innova/Ertiga)', seats: 6, decorated: true, price: 2500, per: '/ vehicle / day' },
  { id: 'tempo', name: 'Tempo Traveller', seats: 12, ac: true, price: 4500, per: '/ vehicle / day' },
  { id: 'mixed', name: 'Mixed Fleet', custom: true, price: null, per: 'quote' },
];

const EVENT_OPTIONS = ['Mehendi', 'Haldi', 'Baraat', 'Reception', 'Phera', 'Vidaai'];

const getFleetCount = (fleetSize) => {
  const map = { '1-3': 2, '4-8': 6, '9-15': 12, 'custom': 0 };
  return map[fleetSize] || 0;
};

const WeddingForm = () => {
  const [city, setCity] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [fleetSize, setFleetSize] = useState('4-8');
  const [vehicleType, setVehicleType] = useState('sedan');
  const [events, setEvents] = useState(['mehendi', 'haldi', 'baraat']);
  const [contactName, setContactName] = useState('');
  const [phone, setPhone] = useState('');
  const [notes, setNotes] = useState('');

  const vehicle = VEHICLE_OPTIONS.find((v) => v.id === vehicleType);
  const fleetCount = getFleetCount(fleetSize);
  const eventDays = Math.max(1, events.length);

  const estimatedTotal = useMemo(() => {
    if (!vehicle || !vehicle.price || fleetCount === 0) return null;
    return vehicle.price * fleetCount * eventDays;
  }, [vehicle, fleetCount, eventDays]);

  const bulkDiscount = estimatedTotal && fleetCount >= 6 ? Math.round(estimatedTotal * 0.1) : 0;
  const finalEstimate = estimatedTotal ? estimatedTotal - bulkDiscount : null;

  const toggleEvent = (evt) => {
    const key = evt.toLowerCase();
    setEvents((prev) =>
      prev.includes(key) ? prev.filter((e) => e !== key) : [...prev, key]
    );
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!city || !eventDate || !phone || !contactName) {
      toast.error('Please fill all required fields');
      return;
    }
    toast.success('Wedding quote request submitted!');
  };

  return (
    <div className="page-body">
      {/* FORM SIDE */}
      <form className="form-side" onSubmit={handleSubmit}>
        <div className="page-title-row">
          <div className="page-badge badge-purple">Wedding Fleet</div>
          <h2 className="page-title">Book your wedding vehicles</h2>
          <p className="page-sub">Affordable decorated fleet for every wedding event — baraat, mehendi, haldi, reception.</p>
        </div>

        <div className="form-grid">
          <div className="fg">
            <label>Wedding City</label>
            <input
              type="text"
              placeholder="e.g. Patna"
              value={city}
              onChange={(e) => setCity(e.target.value)}
              required
            />
          </div>
          <div className="fg">
            <label>Event Date</label>
            <input
              type="date"
              value={eventDate}
              onChange={(e) => setEventDate(e.target.value)}
              required
            />
          </div>
        </div>

        <div className="fg" style={{ marginBottom: 20 }}>
          <label>Fleet Size</label>
          <div className="radio-cards cards-4">
            {FLEET_SIZES.map((fs) => (
              <div
                key={fs.id}
                className={`rc${fleetSize === fs.id ? ' active' : ''}`}
                onClick={() => setFleetSize(fs.id)}
              >
                <div className="rc-label">{fs.label}</div>
                <div className="rc-sub">{fs.sub}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="fg" style={{ marginBottom: 20 }}>
          <label>Vehicle Type</label>
          <div className="fleet-grid">
            {VEHICLE_OPTIONS.map((v) => (
              <div
                key={v.id}
                className={`fleet-card${vehicleType === v.id ? ' active' : ''}`}
                onClick={() => setVehicleType(v.id)}
              >
                <h5>{v.name}</h5>
                <div className="fleet-specs">
                  {v.seats && <span className="fleet-spec">{v.seats} seats</span>}
                  {v.decorated && <span className="fleet-spec">Decorated</span>}
                  {v.ac && <span className="fleet-spec">AC</span>}
                  {v.custom && <span className="fleet-spec">Custom</span>}
                </div>
                {v.price ? (
                  <div className="fleet-price">
                    ₹{v.price.toLocaleString('en-IN')}
                    <span className="fleet-per"> {v.per}</span>
                  </div>
                ) : (
                  <div className="fleet-price">
                    <span className="fleet-per">{v.per}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="fg" style={{ marginBottom: 20 }}>
          <label>Events to Cover</label>
          <div className="event-tags">
            {EVENT_OPTIONS.map((evt) => (
              <span
                key={evt}
                className={`ev-tag${events.includes(evt.toLowerCase()) ? ' active' : ''}`}
                onClick={() => toggleEvent(evt)}
              >
                {evt}
              </span>
            ))}
          </div>
        </div>

        <div className="form-grid">
          <div className="fg">
            <label>Contact Name</label>
            <input
              type="text"
              placeholder="Your name"
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
              required
            />
          </div>
          <div className="fg">
            <label>Mobile Number</label>
            <div className="prefix-wrap">
              <span className="prefix">+91</span>
              <input
                type="tel"
                placeholder="98000 00000"
                value={phone}
                onChange={(e) => setPhone(e.target.value.replace(/\D/g, ''))}
                maxLength={10}
                required
              />
            </div>
          </div>
          <div className="fg span2">
            <label>Additional Notes</label>
            <textarea
              placeholder="Any special requirements..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={3}
            />
          </div>
        </div>

        <div className="submit-area">
          <div className="price-strip">
            <div className="price-strip-left">
              <div className="plabel">Estimated total</div>
              <div className="pnote">{vehicle?.name} × {fleetCount} vehicles × {eventDays} day{eventDays > 1 ? 's' : ''}</div>
            </div>
            <div className="pamount">
              {finalEstimate ? `₹${finalEstimate.toLocaleString('en-IN')}` : 'Quote'}
            </div>
          </div>

          <div className="trust-badges">
            <span className="tb"><span className="tb-dot" />Decorated vehicles</span>
            <span className="tb"><span className="tb-dot" />On-time guarantee</span>
            <span className="tb"><span className="tb-dot" />Wedding coordinator</span>
            <span className="tb"><span className="tb-dot" />UPI or cash</span>
          </div>

          <button type="submit" className="submit-btn">
            Get wedding quote →
          </button>
        </div>
      </form>

      {/* SUMMARY SIDE */}
      <div className="summary-side">
        <div className="summary-card">
          <h4>Booking summary</h4>
          <div className="sum-row">
            <span className="sk">Service</span>
            <span className="sv">Wedding Fleet</span>
          </div>
          <div className="sum-row">
            <span className="sk">City</span>
            <span className="sv">{city || '—'}</span>
          </div>
          <div className="sum-row">
            <span className="sk">First event date</span>
            <span className="sv">{eventDate || '—'}</span>
          </div>
          <div className="sum-row">
            <span className="sk">Fleet</span>
            <span className="sv">{FLEET_SIZES.find((f) => f.id === fleetSize)?.label || '—'} · {vehicle?.name}</span>
          </div>
          <div className="sum-row">
            <span className="sk">Events</span>
            <span className="sv">{events.length > 0 ? events.map((e) => e.charAt(0).toUpperCase() + e.slice(1)).join(', ') : '—'}</span>
          </div>
          <div className="sum-row row-inline">
            <span className="sk">Duration</span>
            <span className="sv">{eventDays} day{eventDays > 1 ? 's' : ''}</span>
          </div>
          <div className="sum-row row-inline">
            <span className="sk">Contact</span>
            <span className="sv">{contactName || '—'}</span>
          </div>

          <div className="sum-fare-label">Estimated fare</div>
          <div className="sum-total">
            <span className="sta">{finalEstimate ? `₹${finalEstimate.toLocaleString('en-IN')}` : 'Quote'}</span>
            <span className="stl">Final price confirmed by coordinator</span>
          </div>
        </div>

        <div className="fare-breakdown">
          <h4>Fare breakdown:</h4>
          <div className="fb-row">
            <span className="fk">{vehicle?.name} × {fleetCount} × {eventDays} day{eventDays > 1 ? 's' : ''}</span>
            <span className="fv">{estimatedTotal ? `₹${estimatedTotal.toLocaleString('en-IN')}` : '—'}</span>
          </div>
          {bulkDiscount > 0 && (
            <div className="fb-row">
              <span className="fk" style={{ color: 'var(--green, #1A7A4A)' }}>Bulk discount (10%)</span>
              <span className="fv" style={{ color: 'var(--green, #1A7A4A)' }}>−₹{bulkDiscount.toLocaleString('en-IN')}</span>
            </div>
          )}
          <div className="fb-row">
            <span className="fk">Decoration</span>
            <span className="fv" style={{ color: 'var(--green, #1A7A4A)' }}>Free</span>
          </div>
          <div className="fb-row">
            <span className="fk">Wedding coordinator</span>
            <span className="fv" style={{ color: 'var(--green, #1A7A4A)' }}>Included</span>
          </div>
          <div className="fb-row">
            <span className="fk">Final estimate</span>
            <span className="fv" style={{ color: 'var(--orange, #E8590A)' }}>{finalEstimate ? `₹${finalEstimate.toLocaleString('en-IN')}` : 'Quote'}</span>
          </div>
        </div>

        <div className="steps-card">
          <h4>What happens next?</h4>
          <div className="step-item">
            <span className="step-num">1</span>
            <div className="step-text">
              <h6>Submit request</h6>
              <p>Tell us your wedding details.</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-num">2</span>
            <div className="step-text">
              <h6>Coordinator calls in 2 hours</h6>
              <p>Final quote &amp; decoration plan.</p>
            </div>
          </div>
          <div className="step-item">
            <span className="step-num">3</span>
            <div className="step-text">
              <h6>Fleet arrives on time</h6>
              <p>Decorated vehicles, on schedule, every event.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WeddingForm;
